/**
 * @file Sets up and returns the tldraw store.
 *
 * 1. If the yStore has data, initialize the tlStore with it, since that will be the most up to date. Otherwise, initialize yStore with tlStore instead.
 * 2. Sync local document changes from tlStore -> yStore
 * 3. Sync remote document changes from yStore -> tlStore
 * 4. Sync yStore awareness/presence changes to tlStore
 *
 * There is a meta object set up to handle underlying schema changes when we upgrade to a new version of tldraw (sometimes the schema bumps).
 * What will happen is that when a client on a newer schema joins an existing room where other clients are on an older schema,
 * the clients on the older schema will be forced to reload the page so they can fetch the latest version of the schema.
 * This is for compatibility reasons, e.g. a new version of tldraw has support for a new shape, older clients would not know how to handle it and crash.
 */

import environment from '@@/bits/environment'
import { isDebugMode } from '@@/bits/flip'
import PadletAttachmentShapeUtil from '@@/bits/tldraw/PadletAttachmentShape'
import PadletHalfEllipseShapeUtil from '@@/bits/tldraw/PadletHalfEllipseShape'
import PadletJamboardDrawShapeUtil from '@@/bits/tldraw/PadletJamboardDrawGroupShape'
import useYProvider from '@@/y-partykit/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { SerializedSchema, TLInstancePresence, TLRecord, TLStoreWithStatus } from 'tldraw'
import {
  computed,
  createPresenceStateDerivation,
  createTLStore,
  defaultShapeUtils,
  defaultUserPreferences,
  getUserPreferences,
  InstancePresenceRecordType,
  react,
  setUserPreferences,
} from 'tldraw'
import { YKeyValue } from 'y-utility/y-keyvalue'
import * as Y from 'yjs'

const HOST = (() => {
  switch (environment) {
    case 'production': {
      if (isDebugMode) {
        const searchParams = new URLSearchParams(window.location.search)
        return searchParams.get('whiteboard_server_host') ?? 'https://partykit.padlet.com'
      }
      return 'https://partykit.padlet.com'
    }
    case 'staging': {
      // Hack: We want to be able to test whiteboard in a staging environment
      // but don't want to necessarily couple it to a specific server yet since we are moving fast.
      const searchParams = new URLSearchParams(window.location.search)
      return searchParams.get('whiteboard_server_host') ?? ''
    }
    case 'development':
    default:
      return 'http://localhost:1999'
  }
})()

export function useTldrawStore({
  wallId,
  userId,
  canIViewShape,
  onError,
}: {
  wallId: string
  userId: string
  canIViewShape: (record: TLRecord) => boolean
  onError: (errorMessage?: string, level?: string) => void
}): { store: TLStoreWithStatus; stopRealtimeUpdates: () => void } {
  const [tlStore] = useState(() => {
    const store = createTLStore({
      shapeUtils: [
        ...defaultShapeUtils,
        PadletAttachmentShapeUtil,
        PadletHalfEllipseShapeUtil,
        PadletJamboardDrawShapeUtil,
      ],
    })

    return store
  })

  const [tlStoreWithStatus, setTlStoreWithStatus] = useState<TLStoreWithStatus>({
    status: 'loading',
  })

  const { yDoc, yStore, meta } = useMemo(() => {
    const yDoc = new Y.Doc()
    // Every peer that defines their store with the same arrName will sync content with this client
    const yArrName = `tl_${wallId}`
    const yArr = yDoc.getArray<{ key: string; val: TLRecord }>(yArrName)
    const yStore = new YKeyValue(yArr)
    const meta = yDoc.getMap<SerializedSchema>('meta')

    return {
      yDoc,
      yStore,
      meta,
    }
  }, [wallId])

  const whiteboardClientId = localStorage.getItem('whiteboardClientId')
  const yProvider = useYProvider({
    host: HOST,
    room: wallId,
    doc: yDoc,
    options: {
      params: {
        wallId,
        userId,
        clientId: whiteboardClientId,
      },
    },
    onDestroy: onError,
  })

  const unsubs = useRef<Array<() => void>>([])

  useEffect(() => {
    setTlStoreWithStatus({ status: 'loading' })

    function handleSync(): void {
      /* -------------------- Document -------------------- */

      // Sync (local) changes from tldraw store -> yjs store
      const handleLocalSync = tlStore.listen(
        function syncStoreChangesToYjsDoc({ changes }) {
          yDoc.transact(() => {
            Object.values(changes.added).forEach((record) => {
              yStore.set(record.id, record)
            })

            Object.values(changes.updated).forEach(([_, record]) => {
              yStore.set(record.id, record)
            })

            Object.values(changes.removed).forEach((record) => {
              yStore.delete(record.id)
            })
          })
        },
        // Only sync user's document changes
        // Presence is synced separately
        { source: 'user', scope: 'document' },
      )

      unsubs.current.push(handleLocalSync)

      // Sync (remote) changes from yjs store -> tldraw store
      const handleRemoteSync = (
        changes: Map<
          string,
          | { action: 'delete'; oldValue: TLRecord }
          | { action: 'update'; oldValue: TLRecord; newValue: TLRecord }
          | { action: 'add'; newValue: TLRecord }
        >,
        transaction: Y.Transaction,
      ): void => {
        if (transaction.local) return

        const toRemove: Array<TLRecord['id']> = []
        const toPut: TLRecord[] = []

        changes.forEach((change, id) => {
          switch (change.action) {
            case 'add':
            case 'update': {
              const record = yStore.get(id)
              if (record == null || !canIViewShape(record)) {
                break
              }
              toPut.push(record)
              break
            }
            case 'delete': {
              toRemove.push(id as TLRecord['id'])
              break
            }
          }
        })

        // put / remove the records in the store
        tlStore.mergeRemoteChanges(() => {
          if (toRemove.length > 0) tlStore.remove(toRemove)
          if (toPut.length > 0) tlStore.put(toPut)
        })
      }

      yStore.on('change', handleRemoteSync)
      unsubs.current.push(() => yStore.off('change', handleRemoteSync))

      /* -------------------- Awareness ------------------- */

      const yClientId = yProvider.awareness.clientID.toString()
      setUserPreferences({ id: yClientId })

      const userPreferences = computed<{
        id: string
        color: string
        name: string
      }>('userPreferences', () => {
        const user = getUserPreferences()
        return {
          id: user.id,
          color: user.color ?? defaultUserPreferences.color,
          name: user.name ?? defaultUserPreferences.name,
        }
      })

      // Create the instance presence derivation
      const presenceId = InstancePresenceRecordType.createId(yClientId)
      const presenceDerivation = createPresenceStateDerivation(userPreferences, presenceId)(tlStore)

      // Set our initial presence from the derivation's current value
      yProvider.awareness.setLocalStateField('presence', presenceDerivation.get())

      // When the derivation change, sync presence to yjs awareness
      const handlePresenceChange = react('when presence changes', () => {
        const presence = presenceDerivation.get()
        requestAnimationFrame(() => {
          yProvider.awareness.setLocalStateField('presence', presence)
        })
      })
      unsubs.current.push(handlePresenceChange)

      // Sync yjs awareness changes to tlStore
      const handlePresenceUpdate = (update: { added: number[]; updated: number[]; removed: number[] }): void => {
        const states = yProvider.awareness.getStates() as Map<number, { presence: TLInstancePresence }>

        const toRemove: Array<TLInstancePresence['id']> = []
        const toPut: TLInstancePresence[] = []

        // Connect records to put / remove
        for (const clientId of update.added) {
          const state = states.get(clientId)
          if (state?.presence != null && state.presence.id !== presenceId) {
            toPut.push(state.presence)
          }
        }

        for (const clientId of update.updated) {
          const state = states.get(clientId)
          if (state?.presence != null && state.presence.id !== presenceId) {
            toPut.push(state.presence)
          }
        }

        for (const clientId of update.removed) {
          toRemove.push(InstancePresenceRecordType.createId(clientId.toString()))
        }

        // put / remove the records in the store
        tlStore.mergeRemoteChanges(() => {
          if (toRemove.length > 0) tlStore.remove(toRemove)
          if (toPut.length > 0) tlStore.put(toPut)
        })
      }
      yProvider.awareness.on('update', handlePresenceUpdate)

      unsubs.current.push(() => yProvider.awareness.off('update', handlePresenceUpdate))

      /* -------------------- Schema verification -------------------- */

      const handleMetaUpdate = (): void => {
        const theirSchema = meta.get('schema')
        if (theirSchema == null) {
          throw new Error('No schema found in the yjs doc')
        }
        // If the shared schema is newer than our schema, the user must refresh
        const newMigrations = tlStore.schema.getMigrationsSince(theirSchema)

        if (!newMigrations.ok || newMigrations.value.length > 0) {
          window.alert('The schema has been updated. Please refresh the page.')
          yDoc.destroy()
        }
      }
      meta.observe(handleMetaUpdate)
      unsubs.current.push(() => meta.unobserve(handleMetaUpdate))

      /* -------------------- Initialization -------------------- */

      // Initialize the tlStore with the yStore if has any records.
      // Otherwise, initialize the yStore with the tlStore records.
      if (yStore.yarray.length > 0) {
        // Replace the store records with the yjs doc records
        const ourSchema = tlStore.schema.serialize()
        const theirSchema = meta.get('schema')
        if (theirSchema == null) {
          throw new Error('No schema found in the yjs doc')
        }

        const unviewableShapes = new Set()
        const remoteRecords = yStore.yarray
          .toJSON()
          .map(({ val }) => val)
          .filter((record) => {
            const canViewShape = canIViewShape(record)
            if (!canViewShape) {
              unviewableShapes.add(record.id)
            }
            return canViewShape
          })

        const migrationResult = tlStore.schema.migrateStoreSnapshot({
          schema: theirSchema,
          store: Object.fromEntries(remoteRecords.map((record) => [record.id, record])),
        })
        if (migrationResult.type === 'error') {
          // if the schema is newer than ours, the user must refresh
          console.error(migrationResult.reason)
          window.alert('The whiteboard schema has been updated. Please refresh the page.')
          return
        }

        yDoc.transact(() => {
          // If a user that cannot see unapproved shapes loads whiteboard,
          // this code block would assume they are records no longer valid due to a migration and delete them.
          // Hence, we need to check if the shape was removed from the records earlier.
          for (const r of remoteRecords) {
            if (migrationResult.value[r.id] != null && !unviewableShapes.has(r.id)) {
              yStore.delete(r.id)
            }
          }
          for (const r of Object.values(migrationResult.value)) {
            yStore.set(r.id, r)
          }
          meta.set('schema', ourSchema)
        })

        tlStore.loadSnapshot({
          store: migrationResult.value,
          schema: ourSchema,
        })
      } else {
        // Create the initial store records
        // Sync the store records to the yjs doc
        yDoc.transact(() => {
          for (const record of tlStore.allRecords()) {
            yStore.set(record.id, record)
          }
          meta.set('schema', tlStore.schema.serialize())
        })
      }

      setTlStoreWithStatus({
        store: tlStore,
        status: 'synced-remote',
        connectionStatus: 'online',
      })
    }

    let hasConnectedBefore = false

    function handleStatusChange({ status }: { status: 'disconnected' | 'connected' }): void {
      if (status === 'disconnected') {
        setTlStoreWithStatus({
          store: tlStore,
          status: 'synced-remote',
          connectionStatus: 'offline',
        })
        return
      }

      yProvider.off('synced', handleSync)

      if (status === 'connected') {
        if (hasConnectedBefore) return
        hasConnectedBefore = true
        yProvider.on('synced', handleSync)
        unsubs.current.push(() => yProvider.off('synced', handleSync))
      }
    }

    yProvider.on('status', handleStatusChange)
    unsubs.current.push(() => yProvider.off('status', handleStatusChange))

    return () => {
      unsubs.current.forEach((fn) => fn())
      unsubs.current.length = 0
    }
  }, [yProvider, yDoc, tlStore, yStore, meta])

  function stopRealtimeUpdates(): void {
    unsubs.current.forEach((fn) => fn())
    unsubs.current.length = 0
    yProvider.destroy()
  }

  return { store: tlStoreWithStatus, stopRealtimeUpdates }
}
